import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listAktivitetshyraSource,
  importAktivitetshyraProduct,
  wipeAllProducts,
} from "@/lib/import.functions";
import { toast } from "sonner";
import { Loader2, Download, RefreshCw, Trash2, Check, ExternalLink, Play, Square } from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/import")({
  component: ImportPage,
});

type SrcProduct = {
  source_url: string;
  slug: string;
  name: string;
  category_slug: string;
  image: string;
  imported: boolean;
};

function ImportPage() {
  const list = useServerFn(listAktivitetshyraSource);
  const importOne = useServerFn(importAktivitetshyraProduct);
  const wipe = useServerFn(wipeAllProducts);

  const [items, setItems] = useState<SrcProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkStopRequested, setBulkStopRequested] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string }>({
    done: 0, total: 0, current: "",
  });
  const [busyUrl, setBusyUrl] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "new" | "imported">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleOne = (url: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });

  const reload = async () => {
    setLoading(true);
    try {
      const r = (await list({})) as SrcProduct[];
      setItems(r);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = items.filter((p) =>
    filter === "all" ? true : filter === "new" ? !p.imported : p.imported,
  );

  const importSingle = async (p: SrcProduct) => {
    setBusyUrl(p.source_url);
    try {
      const r = await importOne({ data: { source_url: p.source_url } });
      toast.success(`${r.status === "created" ? "Importerad" : "Uppdaterad"}: ${r.name}`);
      setItems((prev) =>
        prev.map((it) => (it.source_url === p.source_url ? { ...it, imported: true } : it)),
      );
    } catch (e: any) {
      toast.error(`${p.name}: ${e.message}`);
    } finally {
      setBusyUrl(null);
    }
  };

  const runImport = async (targets: SrcProduct[], emptyMsg: string) => {
    if (targets.length === 0) {
      toast.info(emptyMsg);
      return;
    }
    setBulkRunning(true);
    setBulkStopRequested(false);
    setProgress({ done: 0, total: targets.length, current: "" });
    let done = 0;
    for (const p of targets) {
      if (bulkStopRequested) break;
      setProgress({ done, total: targets.length, current: p.name });
      try {
        await importOne({ data: { source_url: p.source_url } });
        setItems((prev) =>
          prev.map((it) => (it.source_url === p.source_url ? { ...it, imported: true } : it)),
        );
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(p.source_url);
          return next;
        });
      } catch (e: any) {
        toast.error(`${p.name}: ${e.message}`);
      }
      done++;
    }
    setBulkRunning(false);
    setProgress({ done, total: targets.length, current: "" });
    toast.success(`Klart: ${done}/${targets.length} importerade.`);
  };

  const importAll = () =>
    runImport(items.filter((p) => !p.imported), "Inget nytt att importera.");

  const importSelected = () =>
    runImport(
      items.filter((p) => selected.has(p.source_url)),
      "Inget markerat.",
    );

  const toggleAllVisible = () => {
    const visibleUrls = filtered.map((p) => p.source_url);
    const allSelected = visibleUrls.every((u) => selected.has(u));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleUrls.forEach((u) => next.delete(u));
      else visibleUrls.forEach((u) => next.add(u));
      return next;
    });
  };

  const doWipe = async () => {
    if (!confirm("Detta tar bort ALLA produkter från databasen. Är du säker?")) return;
    try {
      await wipe({});
      toast.success("Alla produkter borttagna.");
      setItems((prev) => prev.map((it) => ({ ...it, imported: false })));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <PageHeader title="Importera från aktivitetshyra.se" backTo="/admin/products">
        <button
          onClick={doWipe}
          className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
        >
          <Trash2 className="h-4 w-4" /> Töm alla produkter
        </button>
      </PageHeader>

      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="text-sm">
              <span className="font-bold">{items.length}</span> produkter hittade hos källan.{" "}
              <span className="text-muted-foreground">
                Nya: {items.filter((p) => !p.imported).length} · Redan importerade:{" "}
                {items.filter((p) => p.imported).length}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Importerade produkter sätts som <strong>opublicerade</strong> — slå på dem
              en och en när texterna är granskade.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={reload}
              disabled={loading || bulkRunning}
              className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Uppdatera lista
            </button>
            {!bulkRunning ? (
              <>
                <button
                  onClick={importSelected}
                  disabled={loading || selected.size === 0}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> Importera markerade ({selected.size})
                </button>
                <button
                  onClick={importAll}
                  disabled={loading || items.filter((p) => !p.imported).length === 0}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  <Play className="h-4 w-4" /> Importera alla nya
                </button>
              </>
            ) : (
              <button
                onClick={() => setBulkStopRequested(true)}
                className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
              >
                <Square className="h-4 w-4" /> Stoppa
              </button>
            )}
          </div>
        </div>

        {bulkRunning && (
          <div className="mt-4">
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {progress.done}/{progress.total} — {progress.current}
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {(["all", "new", "imported"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
              filter === f
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground"
            }`}
          >
            {f === "all" ? "Alla" : f === "new" ? "Ej importerade" : "Importerade"}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((p) => selected.has(p.source_url))}
                    onChange={toggleAllVisible}
                    className="h-4 w-4 cursor-pointer"
                  />
                </th>
                <th className="text-left px-3 py-2 font-bold">Produkt</th>
                <th className="text-left px-3 py-2 font-bold hidden md:table-cell">Kategori</th>
                <th className="text-left px-3 py-2 font-bold hidden md:table-cell">Källa</th>
                <th className="text-right px-3 py-2 font-bold">Åtgärd</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.source_url} className="border-t border-border">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(p.source_url)}
                      onChange={() => toggleOne(p.source_url)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="h-10 w-10 rounded object-cover bg-secondary"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary" />
                      )}
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        {p.imported && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-green-700 dark:text-green-400 font-bold uppercase">
                            <Check className="h-3 w-3" /> Importerad
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{p.category_slug}</td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <a
                      href={p.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Visa <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => importSingle(p)}
                      disabled={busyUrl !== null || bulkRunning}
                      className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold disabled:opacity-50"
                    >
                      {busyUrl === p.source_url ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : p.imported ? (
                        <RefreshCw className="h-3 w-3" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      {p.imported ? "Hämta igen" : "Importera"}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                    Inget att visa.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
